import { useCallback, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { downloadXlsx, safeFilePart } from '../../../utils/xlsx';
import { DeviceFilters, fetchDevicePage } from './deviceQuery';

const EXPORT_PAGE_SIZE = 500;
const MAX_PAGES = 40; // 20 000 devices at most

/**
 * The Excel export of the filtered fleet: IMEI, phone, creation date and
 * conf47, one row per device, fetched in pages of 500. Offered only once
 * a client and a device type are both filtered: one fleet, one product.
 */
export const useDevicesExport = (filters: DeviceFilters, clientName: (clientId: string) => string) => {
  const { formatDateTime, t } = useI18n();
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canExport = filters.clients.length > 0 && filters.deviceTypes.length > 0;

  const exportExcel = useCallback(async () => {
    setExporting(true);
    setNotice(null);
    try {
      const all: any[] = [];
      let truncated = false;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const result = await fetchDevicePage(filters, { page, pageSize: EXPORT_PAGE_SIZE, sortField: 'idDevice', sortDirection: 'asc' });
        all.push(...result.items);
        const totalPages = result.pagination?.total_pages ?? 1;
        if (page >= totalPages || result.items.length === 0) break;
        if (page === MAX_PAGES) truncated = true;
      }
      if (all.length === 0) {
        setNotice(t('devices.export.none'));
        return;
      }
      const rows = all.map((d) => [
        d.idDevice,
        d.phone && d.phone !== '000000000000000' ? d.phone : '',
        d.dateTime ? formatDateTime(d.dateTime) : '',
        d.conf47Type || '',
      ]);
      const clientPart = filters.clients.length === 1
        ? clientName(filters.clients[0])
        : filters.clients.length > 1 ? `${filters.clients.length}-clients` : 'tous-clients';
      const typePart = filters.deviceTypes.length ? filters.deviceTypes.join('-') : 'tous-types';
      const stamp = new Date().toISOString().slice(0, 10);
      downloadXlsx(
        `equipements-${safeFilePart(clientPart)}-${safeFilePart(typePart)}-${stamp}.xlsx`,
        t('devices.export.sheet'),
        [t('devices.export.colImei'), t('devices.export.colPhone'), t('devices.export.colCreated'), t('devices.export.colConf47')],
        rows,
      );
      if (truncated) setNotice(t('devices.export.tooMany', { max: MAX_PAGES * EXPORT_PAGE_SIZE }));
    } catch (err: any) {
      console.error('[DEVICES] Excel export failed:', err);
      setNotice(`${t('devices.export.failed')}: ${err?.message || err}`);
    } finally {
      setExporting(false);
    }
  }, [filters, clientName, formatDateTime, t]);

  return { canExport, exporting, notice, exportExcel };
};
